/**
 * A document's status, as a coloured pill.
 *
 * The mapping from status to colour is a pure function, so it is tested rather
 * than eyeballed — a status that silently renders in the "draft" grey because
 * someone added a case and forgot it is a status a shopkeeper misreads at a
 * glance, which is worse than no colour at all.
 */
import { cn } from '../lib/cn';

export type StatusTone = 'neutral' | 'progress' | 'positive' | 'negative';

const TONE: Record<StatusTone, string> = {
  neutral: 'border-slate-700 bg-slate-800/60 text-slate-300',
  progress: 'border-amber-700 bg-amber-950/60 text-amber-200',
  positive: 'border-emerald-700 bg-emerald-950/60 text-emerald-200',
  negative: 'border-red-700 bg-red-950/60 text-red-200',
};

/**
 * Which colour a status gets.
 *
 * Unknown statuses are `neutral`, never `positive`. A new status added to the
 * domain without a colour here must not default to looking issued-and-good.
 */
export function statusTone(status: string): StatusTone {
  switch (status) {
    case 'ISSUED':
    case 'PAID':
    case 'COMPLETED':
    case 'APPROVED':
    case 'RECEIVED':
      return 'positive';
    case 'DRAFT':
    case 'PENDING':
    case 'PARTIALLY_PAID':
    case 'PLACED':
    case 'SENT':
      return 'progress';
    case 'CANCELLED':
    case 'REJECTED':
    case 'OVERDUE':
    case 'EXPIRED':
      return 'negative';
    default:
      return 'neutral';
  }
}

/** A human label, because `PARTIALLY_PAID` is not something to show a customer. */
export function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        TONE[statusTone(status)],
        className,
      )}
    >
      {statusLabel(status)}
    </span>
  );
}
